"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
function PartnerFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 px-6 text-center animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 bg-primary/10 text-primary rounded-none flex items-center justify-center mb-6">
          <CircleCheck className="h-8 w-8" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground mb-2">Request Logged</h3>
        <p className="text-muted-foreground font-default mb-8 max-w-sm">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="w-full sm:w-auto rounded-sm border-border">
          New Media Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="company_name" rules={{
        required: "Outlet name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-foreground font-default">Media Outlet / Publication</FormLabel>
              <FormControl>
                <Input placeholder="ABC News / AFR" className="h-11 bg-background/50 border-border rounded-sm focus-visible:ring-ring transition-colors" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contact_name" rules={{
        required: "Journalist name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-foreground font-default">Journalist Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Smith" className="h-11 bg-background/50 border-border rounded-sm focus-visible:ring-ring transition-colors" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-foreground font-default">Professional Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="j.smith@media.com.au" className="h-11 bg-background/50 border-border rounded-sm focus-visible:ring-ring transition-colors" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-foreground font-default">Direct Line <span className="text-muted-foreground font-normal text-xs ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+61 2 0000 0000" className="h-11 bg-background/50 border-border rounded-sm focus-visible:ring-ring transition-colors" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="partnership_type" rules={{
      required: "Please select an enquiry type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-semibold text-foreground font-default">Enquiry Type</FormLabel>
            <FormControl>
              <div className="relative">
                <select className="flex h-11 w-full min-w-0 rounded-sm border border-input bg-background/50 px-3 py-2 text-base shadow-none transition-colors outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[1px] focus-visible:bg-transparent disabled:cursor-not-allowed disabled:opacity-50 md:text-sm appearance-none font-default" {...field}>
                  <option value="" disabled>Select request type</option>
                  <option value="Interview">Interview Request</option>
                  <option value="Data">Data Clarification</option>
                  <option value="Comment">Official Statement</option>
                  <option value="Other">General Media Enquiry</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-semibold text-foreground font-default">Deadline & Context <span className="text-muted-foreground font-normal text-xs ml-1">(Required)</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Please specify your filing deadline and the specific index or data point you are enquiring about..." className="min-h-[120px] resize-y bg-background/50 border-border rounded-sm focus-visible:bg-transparent transition-colors font-default" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm flex items-start gap-3 text-destructive">
          <CircleAlert className="h-5 w-5 shrink-0 mt-0.5" />
          <p className="text-sm font-medium font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-semibold tracking-tight rounded-sm shadow-none bg-primary text-primary-foreground hover:bg-primary/90 transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Processing Request...
          </> : 'Submit Media Request'}
      </Button>
    </Form>;
}
export default function PartnerWithUs() {
  return <section data-section-id="222017" id="media-enquiries" className="w-full bg-background py-16 md:py-24 relative overflow-hidden dark">
      {}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-primary/5 -skew-x-12 translate-x-20 hidden lg:block pointer-events-none" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-6xl mx-auto rounded-none overflow-hidden shadow-none border border-border bg-card flex flex-col lg:flex-row">
          
          {}
          <div className="lg:w-5/12 relative min-h-[300px] lg:min-h-full hidden md:block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/australian_parliament_house_monochrome.png" alt="Australian institutional architecture" className="absolute inset-0 w-full h-full object-cover grayscale contrast-125" />
            {}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-primary/20 mix-blend-multiply" />
            
            <div className="absolute bottom-0 left-0 p-8 text-white">
              <div className="inline-block bg-destructive px-3 py-1 text-[10px] font-bold tracking-widest uppercase mb-4 rounded-none">Press Only</div>
              <h3 className="text-2xl font-default font-bold mb-2 tracking-tight">Urgent Media Line</h3>
              <p className="text-white/80 font-default text-sm max-w-xs leading-relaxed">
                For immediate comment on index releases or breaking economic data, contact our media desk directly.
              </p>
              <div className="mt-6 font-mono text-sm">
                <p>media@aibureau.org.au</p>
                <p className="mt-1">+61 2 8888 0000</p>
              </div>
            </div>
          </div>

          {}
          <div className="lg:w-7/12 p-8 md:p-12 lg:p-14">
            <div className="max-w-xl mx-auto">
              <div className="mb-10">
                <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4 tracking-tighter">
                  Media Enquiries
                </h2>
                <p className="text-muted-foreground font-default text-lg">
                  The AIB provides independent data and expert commentary for journalists covering Australian household economics.
                </p>
              </div>

              <FormProvider formKey="media_enquiries_form_media_centre" formId="media_enquiries_form_media_centre" sectionName="Media Enquiries" defaultValues={{
              company_name: "",
              contact_name: "",
              email: "",
              phone: "",
              partnership_type: "",
              message: ""
            }} submitText="Submit Media Request" successMessage="Your request has been prioritized. A media officer will respond within 2 hours for urgent deadlines.">
                <PartnerFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}